<script setup>
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import Checkbox from "primevue/checkbox";
import Dropdown from "primevue/dropdown";
import Paginator from "primevue/paginator";
import { ref, computed, watch } from "vue";

const props = defineProps({
    visible: Boolean,
    customers: {
        type: Array,
        default: () => []
    },
    currentFilter: {
        type: String,
        default: 'all'
    }
});

const emit = defineEmits(['update:visible', 'select']);

const searchQuery = ref("");
const selectedCustomers = ref([]);
const selectAll = ref(false);
const currentPage = ref(0);
const rowsPerPage = ref(10);
const sortOption = ref("name");

const sortOptions = [
    { label: "Name", value: "name" },
    { label: "Email", value: "email" },
    { label: "Last Active", value: "lastActive" }
];

const filteredCustomers = computed(() => {
    let filtered = props.customers;
    
    if (searchQuery.value) {
        filtered = filtered.filter(customer => 
            customer.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
            customer.email.toLowerCase().includes(searchQuery.value.toLowerCase())
        );
    }
    
    // Sort
    filtered = [...filtered].sort((a, b) => {
        if (sortOption.value === "name") {
            return a.name.localeCompare(b.name);
        } else if (sortOption.value === "email") {
            return a.email.localeCompare(b.email);
        } else if (sortOption.value === "lastActive") {
            return new Date(b.lastActive) - new Date(a.lastActive);
        }
        return 0;
    });
    
    return filtered;
});

const paginatedCustomers = computed(() => {
    const start = currentPage.value * rowsPerPage.value;
    const end = start + rowsPerPage.value;
    return filteredCustomers.value.slice(start, end);
});

const isCustomerSelected = (customer) => {
    return selectedCustomers.value.some(c => c.id === customer.id);
};

const toggleCustomer = (customer) => {
    const index = selectedCustomers.value.findIndex(c => c.id === customer.id);
    if (index > -1) {
        selectedCustomers.value.splice(index, 1);
    } else {
        selectedCustomers.value.push(customer);
    }
    updateSelectAll();
};

const handleSelectAll = () => {
    if (selectAll.value) {
        selectedCustomers.value = [...filteredCustomers.value];
    } else {
        selectedCustomers.value = [];
    }
};

const updateSelectAll = () => {
    selectAll.value = filteredCustomers.value.length > 0 && 
                      selectedCustomers.value.length === filteredCustomers.value.length;
};

const handleContinue = () => {
    emit('select', selectedCustomers.value, selectAll.value);
    closeDialog();
};

const closeDialog = () => {
    emit('update:visible', false);
    // Reset state
    selectedCustomers.value = [];
    selectAll.value = false;
    searchQuery.value = "";
    currentPage.value = 0;
};

const getTimeAgo = (date) => {
    const now = new Date();
    const lastActive = new Date(date);
    const diffInHours = Math.floor((now - lastActive) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return "1 day ago";
    return `${diffInDays} days ago`;
};

watch(() => props.visible, (newVal) => {
    if (!newVal) {
        // Reset when dialog closes
        selectedCustomers.value = [];
        selectAll.value = false;
        searchQuery.value = "";
        currentPage.value = 0;
    }
});
</script>

<template>
    <Dialog 
        :visible="visible" 
        @update:visible="closeDialog"
        modal 
        :style="{ width: '600px' }"
        :draggable="false"
    >
        <template #header>
            <div class="flex justify-between items-center w-full">
                <h3 class="text-lg font-semibold">Select Recipients</h3>
                <div class="flex gap-2">
                    <Button 
                        label="Cancel" 
                        severity="secondary" 
                        text
                        @click="closeDialog"
                    />
                    <Button 
                        label="Continue" 
                        severity="primary"
                        @click="handleContinue"
                        :disabled="selectedCustomers.length === 0"
                    />
                </div>
            </div>
        </template>

        <div class="space-y-4">
            <!-- Search and Filter Bar -->
            <div class="flex gap-2">
                <IconField iconPosition="left" class="flex-1">
                    <InputIcon class="pi pi-search" />
                    <InputText 
                        v-model="searchQuery" 
                        placeholder="Search recipients..." 
                        class="w-full"
                    />
                </IconField>
                <Button icon="pi pi-filter" severity="secondary" outlined />
                <Dropdown 
                    v-model="sortOption" 
                    :options="sortOptions" 
                    optionLabel="label" 
                    optionValue="value"
                    placeholder="Sort"
                    class="w-32"
                />
            </div>

            <!-- Select All -->
            <div class="flex items-center gap-2 pb-2 border-b">
                <Checkbox 
                    v-model="selectAll" 
                    @change="handleSelectAll"
                    :binary="true"
                />
                <label class="text-sm">
                    Select All Recipients ({{ filteredCustomers.length }})
                </label>
                <span v-if="selectedCustomers.length > 0" class="text-sm text-gray-500 ml-auto">
                    {{ selectedCustomers.length }} selected
                </span>
            </div>

            <!-- Customer List -->
            <div class="space-y-2">
                <div 
                    v-for="customer in paginatedCustomers" 
                    :key="customer.id"
                    class="flex items-center gap-3 p-3 hover:bg-gray-50 rounded cursor-pointer"
                    @click="toggleCustomer(customer)"
                >
                    <Checkbox 
                        :modelValue="isCustomerSelected(customer)"
                        :binary="true"
                        @click.stop
                        @change="toggleCustomer(customer)"
                    />
                    <div class="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                        <i class="pi pi-user text-gray-600"></i>
                    </div>
                    <div class="flex-1">
                        <div class="font-medium">{{ customer.name }}</div>
                        <div class="text-sm text-gray-500">{{ customer.email }}</div>
                    </div>
                    <div class="text-sm text-gray-500">
                        Last active: {{ getTimeAgo(customer.lastActive) }}
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div class="flex items-center justify-between pt-4 border-t">
                <span class="text-sm text-gray-600">
                    Showing {{ currentPage * rowsPerPage + 1 }}-{{ 
                        Math.min((currentPage + 1) * rowsPerPage, filteredCustomers.length) 
                    }} of {{ filteredCustomers.length }} recipients
                </span>
                <Paginator 
                    :first="currentPage * rowsPerPage"
                    :rows="rowsPerPage"
                    :totalRecords="filteredCustomers.length"
                    :rowsPerPageOptions="[10, 20, 50]"
                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                    @page="(e) => currentPage = e.page"
                />
            </div>
        </div>
    </Dialog>
</template>